import React from 'react';
import { useTranslation } from 'react-i18next';

import type { UnitType } from '@r6digital/rapidstor-sdk/domain';
import type Location from '@r6digital/rapidstor-types/react/entities/Location';

import useRapidStorTracking from '@/core/hooks/useRapidStorTracking';
import Button from '@/standard-unit-listing/components/elements/button';
import classes from '@/standard-unit-listing/components/unit-type/styles.module.scss';

import { useSite } from '../../sdk/useSite';

export interface CTAGroupProps {
    unitType: UnitType;
    location: Location;
    onBookOnline?: (unitType: UnitType) => void;
    onSendEnquiry?: (unitType: UnitType) => void;
    /** `'mobile'` / `'desktop'` render responsively; omit to always show. */
    view?: 'mobile' | 'desktop';
}

/**
 * The call-to-action row on a unit type card.
 *
 * Which buttons appear comes from `Settings.journey()`, and their operator-configurable copy from
 * `Settings.labels()` — including the corporation-level overrides, which the bound translator
 * resolves.
 */
function CTAGroup({
    unitType, location, onBookOnline, onSendEnquiry, view,
}: CTAGroupProps) {
    const { t } = useTranslation();
    const { trackEvent } = useRapidStorTracking();
    const { settings } = useSite(location);
    const journey = settings.journey();
    const labels = settings.labels();

    let viewClass = '';
    if (view === 'mobile') viewClass = classes.mobile;
    else if (view === 'desktop') viewClass = classes.desktop;

    const available = unitType.isAvailable;
    const showBookOnline = available && (journey.allowReservation() || journey.allowMoveIn());

    const callSiteManager = (
        label: React.ReactNode,
        type: 'primary' | 'outlinePrimaryOnWhite',
    ) => (
        <a
            href={`tel:${location.contactPhone}`}
            onClick={() => {
                trackEvent('call_site_manager_clicked', {});
            }}
        >
            <Button type={type} size="medium">
                {label}
            </Button>
        </a>
    );

    return (
        <div className={`${classes.ctaGroup} ${viewClass}`}>
            {available ? (
                <>
                    {showBookOnline ? (
                        <Button
                            type="primary"
                            size="medium"
                            onclick={() => onBookOnline?.(unitType)}
                        >
                            {t('standardUnitListing:unitType:bookOnline')}
                        </Button>
                    ) : null}
                    {callSiteManager(
                        t('standardUnitListing:unitType:callSiteManager'),
                        showBookOnline ? 'outlinePrimaryOnWhite' : 'primary',
                    )}
                    {journey.allowSaveQuote() && (
                        <Button
                            type="black"
                            size="medium"
                            onclick={() => onSendEnquiry?.(unitType)}
                        >
                            {labels.saveQuoteButton()}
                        </Button>
                    )}
                </>
            ) : (
                callSiteManager(labels.noAvailableUnits(), 'primary')
            )}
        </div>
    );
}

export default CTAGroup;
